"use client";

/**
 * Searchable product catalog picker with search bar, results list, and browse link.
 *
 * Extracted from `compass-intake.jsx:423-538` (`.cs-pick`, `.cs-searchbar`,
 * `.cs-row`, `.cs-logo`, `.cs-browse` classes).
 */

import { useEffect, useState } from "react";
import { Search, X } from "../lib/icons";
import { LoadingSequence } from "./LoadingSequence";

const BG_PALETTE = [
  "#172746",
  "#1e3a5f",
  "#2d4a6f",
  "#3b5998",
  "#4a6fa5",
  "#5b7fb8",
  "#2c3e50",
  "#34495e",
];

const FG_PALETTE = [
  "#e7fe7c",
  "#7cffe7",
  "#fe7ce7",
  "#e77cfe",
  "#7ce7fe",
  "#fefe7c",
  "#7cfefe",
  "#fee77c",
];

function nameHash(name: string): number {
  return name.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
}

export interface ProductSearchProduct {
  /** Stable identity. Used for React keys and selection — names are not unique. */
  id: string;
  name: string;
  category?: string;
  /** Optional icon URL. Falls back to a deterministic coloured initial. */
  iconUrl?: string;
}

export interface ProductSearchProps {
  /**
   * Products to display. Rendered as-is — filtering is the caller's
   * responsibility (server-side or via a local useMemo before passing).
   */
  products: ProductSearchProduct[];
  /** Currently selected product IDs. */
  selected?: string[];
  /** Called when a product row is clicked. */
  onSelect?: (product: ProductSearchProduct) => void;
  /** Called when the user clicks "Skip". */
  onSkip?: () => void;
  /** Current search query (controlled). Used only to drive the input value and empty-state message. */
  searchQuery?: string;
  /** Called when the search query changes. */
  onSearchChange?: (q: string) => void;
  /** Whether results for the current query are still being fetched. */
  isSearching?: boolean;
  /** Total catalog count for the browse link text. */
  catalogCount?: number;
}

/**
 * Product search panel: search bar → filtered results list → browse link.
 */
export function ProductSearch({
  products,
  selected = [],
  onSelect,
  onSkip,
  searchQuery = "",
  onSearchChange,
  isSearching = false,
  catalogCount,
}: ProductSearchProps) {
  const [visibleSearchQuery, setVisibleSearchQuery] = useState<string | null>(
    null,
  );
  const showSearching = isSearching && visibleSearchQuery === searchQuery;

  useEffect(() => {
    if (!isSearching) {
      setVisibleSearchQuery(null);
      return;
    }

    const timer = setTimeout(() => setVisibleSearchQuery(searchQuery), 1_000);
    return () => clearTimeout(timer);
  }, [isSearching, searchQuery]);

  return (
    <div className="flex flex-col gap-[10px]">
      {/* Search bar */}
      <div
        className={[
          "flex items-center gap-[9px]",
          "bg-(--bg-white) border border-(--border-default)",
          "rounded-(--radius-field)",
          "px-[13px] py-[11px]",
        ].join(" ")}
      >
        <span className="flex-shrink-0 text-(--fg-muted)">
          <Search size={15} />
        </span>
        <input
          type="text"
          className={[
            "flex-1 min-w-0 border-0 outline-none bg-transparent",
            "font-[family-name:var(--font-body)] text-[14px] text-(--fg-primary)",
            "placeholder:text-(--fg-muted)",
          ].join(" ")}
          placeholder="Search vendors…"
          value={searchQuery}
          onChange={(e) => onSearchChange?.(e.target.value)}
        />
        {searchQuery && (
          <button
            type="button"
            className="flex-shrink-0 bg-transparent border-0 text-(--fg-subtle) cursor-pointer flex hover:text-(--fg-secondary)"
            onClick={() => onSearchChange?.("")}
            aria-label="Clear search"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Results — rows stay visible while a new request is in flight, so typing
          does not blank the list on every keystroke. */}
      {isSearching && showSearching && products.length === 0 && (
        <LoadingSequence
          variant="inline"
          label="Searching…"
          estimate={{ kind: "quick", seconds: 5 }}
        />
      )}

      {products.length > 0 && (
        <div className="flex flex-col gap-[7px]">
          {products.map((p) => {
            const isOn = selected.includes(p.id);
            const hash = nameHash(p.name);
            const bg = BG_PALETTE[hash % BG_PALETTE.length] ?? "#172746";
            const fg = FG_PALETTE[hash % FG_PALETTE.length] ?? "#e7fe7c";
            const mark = p.name.charAt(0).toUpperCase();
            return (
              <button
                key={p.id}
                type="button"
                className={[
                  "flex items-center gap-3 text-left",
                  "bg-(--bg-white) border rounded-[6px]",
                  "px-[13px] py-[11px]",
                  "cursor-pointer transition-all duration-300",
                  "font-[family-name:var(--font-body)]",
                  isOn
                    ? "border-(--fg-brand) shadow-[inset_0_0_0_1px_var(--fg-brand)]"
                    : "border-(--border-subtle) hover:border-(--border-strong) hover:bg-(--bg-sky)",
                ].join(" ")}
                onClick={() => onSelect?.(p)}
              >
                <span
                  className="flex-shrink-0 w-[36px] h-[36px] rounded-[6px] flex items-center justify-center text-[16px] font-bold overflow-hidden"
                  style={{ background: bg, color: fg }}
                >
                  {p.iconUrl ? (
                    <img
                      src={p.iconUrl}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    mark
                  )}
                </span>
                <span className="flex flex-col flex-1 min-w-0">
                  <span className="text-[14px] font-semibold text-(--fg-primary)">
                    {p.name}
                  </span>
                  {p.category && (
                    <span className="text-[12px] text-(--fg-muted)">
                      {p.category}
                    </span>
                  )}
                </span>
                <span
                  className={[
                    "flex-shrink-0 w-[18px] h-[18px]",
                    "rounded-full border-[1.5px]",
                    "flex items-center justify-center ml-auto",
                    isOn ? "border-(--fg-brand)" : "border-(--border-strong)",
                  ].join(" ")}
                >
                  {isOn && (
                    <span className="w-[9px] h-[9px] rounded-full bg-(--brand-navy)" />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!isSearching && products.length === 0 && searchQuery.trim() && (
        <div className="text-center py-6 text-[13.5px] text-(--fg-secondary)">
          No vendors match &ldquo;{searchQuery}&rdquo;
        </div>
      )}

      {/* Browse link */}
      {catalogCount != null && (
        <button
          type="button"
          className={[
            "inline-flex items-center gap-2 self-start",
            "text-[13px] font-semibold text-(--fg-brand)",
            "bg-transparent border-0 cursor-pointer",
            "hover:underline hover:underline-offset-2",
          ].join(" ")}
          onClick={onSkip}
        >
          Browse all {catalogCount} vendors
        </button>
      )}
    </div>
  );
}
